/**
 * Bellou Cart Drawer Enhancements
 *
 * - Progress: gratis verzending vanaf €49,95, shown as "vanaf €50" (keep in sync with snippets/bellou-cart-progress.liquid
 *   and the automatic free-shipping discount in Shopify)
 * - Suggestions ("Mooi samen met ..."): rebuilt from the cart contents whenever the cart changes
 */

(function () {
  'use strict';

  const FREE_SHIP_THRESHOLD = 4995;  // €49,95 in cents, so a single €49,95 piece ships free
  const FREE_SHIP_LABEL = 5000;      // the site promises "gratis vanaf €50"

  function formatMoney(cents) {
    const euros = cents / 100;
    return '€' + euros.toFixed(2).replace('.', ',');
  }

  function buildProgressMessage(itemCount, currentTotal) {
    const eurosToShip = Math.max(0, FREE_SHIP_THRESHOLD - currentTotal);
    const highlight = (text) => `<span class="bellou-progress__highlight">${text}</span>`;

    if (itemCount === 0) {
      return `Gratis verzending vanaf ${formatMoney(FREE_SHIP_LABEL)}`;
    }
    if (currentTotal >= FREE_SHIP_THRESHOLD) {
      return 'Je bestelling wordt gratis verzonden';
    }
    return `Nog ${highlight(formatMoney(eurosToShip))} tot gratis verzending`;
  }

  // The bar can appear twice on /cart (cart page and drawer), so update every instance.
  function updateProgressUI(itemCount, currentTotal) {
    document.querySelectorAll('[data-bellou-progress]').forEach((container) => updateProgressBar(container, itemCount, currentTotal));
  }

  function updateProgressBar(container, itemCount, currentTotal) {
    container.dataset.itemCount = String(itemCount);
    container.dataset.currentTotal = String(currentTotal);

    const messageEl = container.querySelector('[data-progress-message]');
    if (messageEl) {
      messageEl.innerHTML = buildProgressMessage(itemCount, currentTotal);
    }

    const overall = Math.min(100, (currentTotal / FREE_SHIP_THRESHOLD) * 100);
    const barEl = container.querySelector('[data-progress-bar]');
    if (barEl) {
      barEl.style.width = overall + '%';
    }
  }

  let lastCartSignature = null;

  // The suggestions depend on what is in the cart, so re-render them whenever the contents change.
  function cartChanged(cart) {
    const signature = cart.items.map((item) => item.variant_id + 'x' + item.quantity).join(',');
    if (lastCartSignature === null) {
      lastCartSignature = signature;
      return false;
    }
    if (lastCartSignature !== signature) {
      lastCartSignature = signature;
      return true;
    }
    return false;
  }

  async function fetchAndUpdateCart() {
    try {
      const response = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
      if (!response.ok) throw new Error('Cart fetch failed: ' + response.status);
      const cart = await response.json();

      updateProgressUI(cart.item_count, cart.total_price);

      if (cartChanged(cart)) {
        refreshCartDrawer();
      }

      return cart;
    } catch (err) {
      console.warn('[Bellou] Could not fetch cart:', err);
      return null;
    }
  }

  async function refreshCartDrawer() {
    try {
      const response = await fetch(window.location.pathname + '?section_id=' + getCartSectionId(), {
        headers: { 'Accept': 'text/html' }
      });
      if (!response.ok) return;
      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      const newUpsell = doc.querySelector('.bellou-upsell');
      const currentUpsell = document.querySelector('.bellou-upsell');
      if (newUpsell && currentUpsell) {
        currentUpsell.replaceWith(newUpsell);
      } else if (newUpsell && !currentUpsell) {
        const items = document.querySelector('.cart-drawer__items');
        if (items && items.parentNode) {
          items.parentNode.insertBefore(newUpsell, items.nextSibling);
        }
      } else if (!newUpsell && currentUpsell) {
        currentUpsell.remove();
      }
    } catch (err) {
      console.warn('[Bellou] Could not refresh upsell:', err);
    }
  }

  function getCartSectionId() {
    const el = document.querySelector('cart-items-component[data-section-id]');
    return el ? el.dataset.sectionId : '';
  }

  async function handleUpsellAdd(button) {
    const variantId = button.dataset.variantId;
    if (!variantId) return;

    button.setAttribute('data-loading', 'true');
    button.disabled = true;

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          items: [{ id: variantId, quantity: 1 }],
          sections: [getCartSectionId()].filter(Boolean).join(',')
        })
      });

      if (!response.ok) throw new Error('Add to cart failed: ' + response.status);

      // On the cart page the item list is server-rendered: reload so the new piece shows up.
      if (window.location.pathname.replace(/\/$/, '').endsWith('/cart')) {
        window.location.reload();
        return;
      }

      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      await fetchAndUpdateCart();
    } catch (err) {
      console.warn('[Bellou] Could not add upsell item:', err);
      button.removeAttribute('data-loading');
      button.disabled = false;
    }
  }

  function handleUpsellToggle(toggle) {
    const upsell = toggle.closest('[data-bellou-upsell]');
    if (!upsell) return;
    const isCollapsed = upsell.getAttribute('data-collapsed') === 'true';
    upsell.setAttribute('data-collapsed', isCollapsed ? 'false' : 'true');
    toggle.setAttribute('aria-expanded', isCollapsed ? 'true' : 'false');
    try {
      localStorage.setItem('bellou_upsell_collapsed', isCollapsed ? 'false' : 'true');
    } catch (e) {}
  }

  function restoreUpsellState() {
    try {
      const saved = localStorage.getItem('bellou_upsell_collapsed');
      if (saved === 'false') {
        const upsell = document.querySelector('[data-bellou-upsell]');
        if (upsell) {
          upsell.setAttribute('data-collapsed', 'false');
          const toggle = upsell.querySelector('[data-bellou-upsell-toggle]');
          if (toggle) toggle.setAttribute('aria-expanded', 'true');
        }
      }
    } catch (e) {}
  }

  function init() {
    fetchAndUpdateCart();

    document.addEventListener('click', (event) => {
      const addBtn = event.target.closest('[data-bellou-upsell-add]');
      if (addBtn) {
        event.preventDefault();
        event.stopPropagation();
        handleUpsellAdd(addBtn);
        return;
      }

      const toggleBtn = event.target.closest('[data-bellou-upsell-toggle]');
      if (toggleBtn) {
        event.preventDefault();
        event.stopPropagation();
        handleUpsellToggle(toggleBtn);
        return;
      }
    });

    const cartEvents = ['cart:updated', 'cart:refresh', 'cart-update', 'cart:change'];
    cartEvents.forEach((eventName) => {
      document.addEventListener(eventName, () => {
        setTimeout(() => {
          fetchAndUpdateCart();
          restoreUpsellState();
        }, 100);
      });
    });

    document.addEventListener('dialog:opened', (event) => {
      if (event.target.closest('cart-drawer-component')) {
        fetchAndUpdateCart();
        restoreUpsellState();
      }
    });

    const cartDrawer = document.querySelector('cart-drawer-component');
    if (cartDrawer) {
      const observer = new MutationObserver(() => {
        if (window._bellouMutationFrame) return;
        window._bellouMutationFrame = requestAnimationFrame(() => {
          window._bellouMutationFrame = null;
          fetchAndUpdateCart();
          restoreUpsellState();
        });
      });
      observer.observe(cartDrawer, { childList: true, subtree: true });
    }

    restoreUpsellState();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();